import React, { useContext, useRef, useEffect, useState } from 'react';
import styled from 'styled-components/macro';
import Draggable from 'react-draggable';
import TextareaAutosize from 'react-textarea-autosize';
import ContentEditable from 'react-contenteditable';
import _ from 'lodash';

import { fontkoKR, fontDefault } from '@mirinae/react-ui';
import EditorContext from '@mirinae/lessons/editor/modules/contexts/editor';
import GlobalContext, { SyncContext } from '@mirinae/lessons/modules/contexts/global';
import { computeAnimationLayout } from '@mirinae/lessons/components/lesson/elements/PatternAnimation';
import { colorMap } from '@mirinae/lessons/modules/defines/styles';
import PopupTitle from '@mirinae/lessons/components/common/PopupTitle';
import AnimContextMenu from './AnimContextMenu';
import { TextField, SmallButton } from '../UI';

const { escapeRegExp } = _;

const AnimationEditorWrapper = styled.div`
    position: absolute;
    ${props => !props.expanded && 'max-width: 450px;'}
    overflow-x: hidden;
    top: 300px;
    left: 400px;
    z-index: 1000;
    background: white;
    border-radius: 6px;
    box-shadow: 0 3px 1px -2px rgb(0 0 0 / 20%), 0 2px 2px 0 rgb(0 0 0 / 14%), 0 1px 5px 0 rgb(0 0 0 / 12%);
`;

const EditorTitleBar = styled(PopupTitle)`
    font-size: 12px;
    background: #dddcdc;
    height: 18px;
    margin: 0;
    padding: 6px;
    border-top-left-radius: 6px;
    border-top-right-radius: 6px;
    justify-content: space-between;
    
    span {
        font-family: Webdings;
        color: #868686;
        font-size: 16px;
        padding: 0 2px 3px 2px;
        cursor: pointer;
    }
`;

const Step = styled.div`
    display: table-row;
    background: ${props => (props.highlight ? '#fff8f8' : 'none')};
`;

const StepLabel = styled.div`
    display: table-cell;
`;

const Cell = styled.div`
  display: table-cell;
  border: thin solid #efefef;
  vertical-align: middle;
  min-width: 40px;
`;

const SectionCell = styled(Cell)`
`;

const SectionContents = styled.div`
    display: flex;
    position: relative;
    flex-direction: row;
    justify-content: space-between;
    align-items: center;
`;

const SeparatorIcon = styled.div`
    font-family: Webdings;
    color: #c1c1c1;
    font-size: 16px;
    cursor: pointer;
    margin: 0 -10px;
    white-space: pre;
`;

const FieldTitle = styled(ContentEditable)`
    font-size: 12px;
    font-weight: bold;
    text-align: left;
    padding: 0px 5px;
    
    &:focus {
        background: aliceblue;
    }
`;

const SectionText = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    margin: 0 15px;
`;

const TextParts = styled.div`
    display: flex;
    width: fit-content;
`;

const TextPartWrapper = styled.div`
    position: relative;
`;

const TextPart = styled(ContentEditable)`
    font-family: ${props => (props.lang === 'korean' ? fontkoKR : fontDefault)};
    font-size: ${props => (props.lang === 'korean' ? 18 : 12)}px;
    color: ${props => colorMap[props.part.color]};
    white-space: pre;
    border: thin solid white;
    ${props => props.part.value === '' && 'min-width: 0.5em;'}
    ${props => (props.editor.animContextMenu.context && props.editor.animContextMenu.context.part === props.part 
            || props.editor.animationEditor.showCommandsFor === props.part) && 'border: 1.01px dotted darkred;'}
    &:focus {
        background: aliceblue;
    }
    &:hover {
        border: ${props => (props.focusedfield.current ? 'thin solid white' : '1.01px dotted darkred')};    
    }
`;

const PartCommandsBox = styled.div`
    position: absolute;
    bottom: 26px;
    left: 10px;
    background: white;
    border-radius: 4px;
    box-shadow: 0 3px 1px -2px rgb(0 0 0 / 20%), 0 2px 2px 0 rgb(0 0 0 / 14%), 0 1px 5px 0 rgb(0 0 0 / 12%);
`;

const PartCommandText = styled(ContentEditable)`
    font-size: 12px;
    padding: 4px 4px;
    //width: ${props => props.part.command.length * 1.5}ch;
    white-space: nowrap;
    &:focus {
        background: aliceblue;
    }
`;

const CommandsBtn = styled.div`
  position: absolute;
  bottom: 20px;
  font-size: 12px;
  left: 0px;
  cursor: pointer;
  color: #74a7da;
`;

const NarrationsCell = styled(Cell)`
    padding-left: 13px;
`;

const NarrationsList = styled.ol`
    list-style: decimal;
    padding-left: 13px;
`;

const Narration = styled.li`
    max-width: 320px;
    font-size: 12px;
    & + & {
        padding-top: 6px;
    }
    ::marker {
        text-align: start;
    }
`;

const NarrationText = styled(TextareaAutosize)`
    font-size: 12px;
    width: 300px;
    overflow: hidden;
    display: block;
    &:focus {
        background: aliceblue;
    }
`;

const StepsTable = styled.div`
  display: table;
  border-collapse: collapse;
  border: thin solid #e0e0e0;
  margin: 6px;
  
  ${Step}:hover {
    // background-color: #f6f6f6;
  }

  ${Cell} {
    padding: 3px 6px;
    // border-bottom: thin solid #ebebeb;
  }
`;

const ToolBar = styled.div`
    display: flex;
    flex-direction: row;
    align-content: center;
    justify-content: space-between;
    padding: 7px 6px 0px 12px;
`;

const Hints = styled.div`
    align-self: center;
    font-size: 10px;
    margin: 2px 0px 8px;
    white-space: pre;
`;

const SRWrapper = styled.div`
    position: relative;
`;

const SRPopup = styled.div`
    display: flex;
    flex-direction: column;
    justify-content: center;
    position: absolute;
    background: white;
    top: 30px; // ${props => props.top}px;
    left: 10px;
    border: thin solid lightgray;
    box-shadow: 0 3px 1px -2px rgb(0 0 0 / 20%), 0 2px 2px 0 rgb(0 0 0 / 14%), 0 1px 5px 0 rgb(0 0 0 / 12%);
    width: 400px;
    padding: 6px 15px;
    z-index: 1000;
`;

const SRPopupButttons = styled.div`
    display: flex;
    flex-direction: row;
    justify-content: space-between;
`;

export const SearchAndReplace = ({ element, stepFields, onReplace }) => {
    const editor = useContext(EditorContext);
    const [show, setShow] = useState(false);
    const [haveIput, setHaveInput] = useState(false);
    const findRef = useRef(null);
    const replRef = useRef(null);
    const srwRef = useRef(null);

    useEffect(() => {
        document.addEventListener('keydown', handleKeyUp);
        return () => document.removeEventListener('keydown', handleKeyUp);
    }, [show]);

    useEffect(() => {
        if (show && findRef.current)
            findRef.current.focus();
    }, [show]);

    const handleKeyUp = (e) => {
        if ((e.metaKey || e.ctrlKey) && e.key === 'f') {
            e.preventDefault();
            e.stopPropagation();
            setShow(true);
        } else if (show && e.key === 'Enter') {
            handleReplace();
            e.preventDefault();
            e.stopPropagation();
        }
    };

    const handleReplace = () => {
        const replPart = (f, pn, i, p) => {
            if (p.value.indexOf(findRef.current.value) >= 0) {
                const newValue = p.value.replace(new RegExp(escapeRegExp(findRef.current.value), 'g'), replRef.current.value);
                editor.updateElementFieldPart(element, f, pn, i, 'value', newValue);
            }
            if (p.command && p.command.indexOf(findRef.current.value) >= 0) {
                const newCommand = p.command.replace(new RegExp(escapeRegExp(findRef.current.value), 'g'), replRef.current.value);
                editor.updateElementFieldPart(element, f, pn, i, 'command', newCommand);
            }
        };
        stepFields.forEach(f => {
            f.parts && Object.getOwnPropertyNames(f.parts).forEach(pn => {
                f.parts[pn].forEach((p, i) => replPart(f, pn, i, p));
            });
        });
        element.fields.forEach(f => {
            f.parts && Object.getOwnPropertyNames(f.parts).forEach(pn => {
                f.parts[pn].forEach((p, i) => replPart(f, pn, i, p));
            });
        });
        editor.commitNodeUpdate(element);
        onReplace();
        setShow(false);
    };

    return (
        <SRWrapper ref={srwRef}>
            <SmallButton variant="outlined" onClick={() => setShow(true)}>Search and Replace</SmallButton>
            { show && (
                <Draggable
                    handle="#sr-popup" bounds="#root" axis="both" defaultPosition={{ x: 0, y: 0 }}>
                    <SRPopup>
                        <div id="sr-popup" style={{ height: 10, backgroundColor: 'lightGray', cursor: 'move' }} />
                        <TextField size="small" inputRef={findRef} onChange={() => setHaveInput(true)} label="Find" />
                        <TextField size="small" inputRef={replRef} onChange={() => setHaveInput(true)} label="Replace" />
                        <SRPopupButttons>
                            <SmallButton variant="outlined" onClick={() => setShow(false)}>Cancel</SmallButton>
                            <SmallButton variant="outlined" defaultBtn onClick={handleReplace} disabled={!haveIput}>Replace all</SmallButton>
                        </SRPopupButttons>
                    </SRPopup>
                </Draggable>
            )}
        </SRWrapper>
    );
};

const AnimationEditor = () => {
    const editor = useContext(EditorContext);
    const global = useContext(GlobalContext);
    const [sync] = useContext(SyncContext);
    const [stepFields, setStepFields] = useState([]);
    const [expanded, setExpanded] = useState(true);
    const focusedField = useRef(null);
    const editorRef = useRef(null);
    const menuRef = useRef(null);
    const textChanged = useRef(false);
    const [draw, setDraw] = useState(1);
    const [highlightField, setHighlightField] = useState(null);
    const redraw = () => setDraw(draw + 1);
    const element = editor.animationEditor.element;
    const example = element.fields.find(f => f.parts && f.example);

    useEffect(() => {
        if (element) {
            // construct computed layout, getting back a clone of the original element's fields
            const [fields, maxWidth, hasSubtext] = computeAnimationLayout(element);
            if (fields.find(f => f.changed)) {
                fields.forEach(f => { delete f.changed; });
                editor.commitNodeUpdate(element);
            }
            setStepFields(fields);
        }
    }, [editor.animationEditor.element, draw]);

    useEffect(() => {
        document.addEventListener('keydown', handleKeyUp);
        document.addEventListener('mousedown', handleMouseDown);
        return () => {
            document.removeEventListener('keydown', handleKeyUp);
            document.removeEventListener('mousedown', handleMouseDown);
        };
    }, []);

    const handleMouseDown = (e) => {
        // detect clicks outside the editor, collapse if so
        if (document.contains(e.target)
            && !editor.animContextMenu.open
            && !((editorRef.current && (editorRef.current === e.target || editorRef.current.contains(e.target)))
                || menuRef.current)) {
            document.activeElement.blur();
            setExpanded(false);
        }
    };

    const handleKeyUp = (e) => {
        if ((e.metaKey || e.ctrlKey) && (e.key === 'u' || e.key === 's' || e.key === 'r')) {
            document.activeElement.blur();
            e.preventDefault();
            e.stopPropagation();
            setExpanded(false);
            global.update({ playerUpdateTrigger: global.playerUpdateTrigger + 1 });
        } else if (e.key === 'Enter') {
            document.activeElement.blur();
        }
    };

    useEffect(() => {
        const currentField = sync.animStep && stepFields.find(f => f.name.toLowerCase() === sync.animStep.toLowerCase());
        if (currentField && currentField.parts) {
            setHighlightField(currentField);
            // currentField.collapsed = false;
            // redraw();
            // currentField.ref.scrollIntoView({ behavior: "smooth", block: "start", inline: "start" });
        }
    }, [stepFields, sync.animStep]);

    const onTextChange = (e) => {
        textChanged.current = true;
    };

    const fieldTitleChanged = (e, f) => {
        if (textChanged.current) {
            f.label = e.target.textContent;
            const realField = element.fields[f.index];
            realField.label = e.target.textContent;
            realField.states = [e.target.textContent];
            if (!f.example)
                realField.name = e.target.textContent;
            editor.commitNodeUpdate(element);
            textChanged.current = false;
        }
    };

    const narrationChanged = (e, field, n, i) => {
        if (textChanged.current) {
            n.value = e.target.value;
            const realField = element.fields[field.index];
            realField.parts.narration[i].value = n.value;
            // temp dropping of syle & links, need to add style editor to anim-editor!!
            realField.parts.narration[i].styles = [];
            realField.parts.narration[i].links = [];
            editor.commitNodeUpdate(element);
            textChanged.current = false;
        }
    };

    const onContextMenu = (e, context) => {
        e.preventDefault();
        e.stopPropagation();
        document.activeElement.blur();
        editor.update({ animContextMenu: { open: true, context, e } });
    };

    const Section = ({ section, field }) => {
        const [hover, setHover] = useState(null);

        const SectionTextParts = ({ lang, parts }) => {
            if (parts.length === 0) {
                // parts.push({ index: 0, value: '', color: 'addition', emptySection: true })
            }

            const textPartChanged = (e, p) => {
                focusedField.current = null;
                if (textChanged.current) {
                    p.value = e.target.textContent;
                    // // also update real field parts
                    const [realField, pn] = [element.fields[field.index], `${lang}Parts`];
                    realField.parts[pn][p.partIndex].value = p.value;
                    //editor.updateElementFieldPart(element, f, pn, p.partIndex, 'value', p.value);
                    editor.commitNodeUpdate(element);
                    textChanged.current = false;
                }
            };

            const setCommandPart = (p) => editor.update({ animationEditor: { ...editor.animationEditor, showCommandsFor: p } });

            const PartCommands = ({ part }) => {
                const onCommandBlur = (e) => {
                    if (textChanged.current) {
                        part.command = e.target.textContent;
                        const [realField, pn] = [element.fields[field.index], `${lang}Parts`];
                        realField.parts[pn][part.partIndex].command = part.command;
                        editor.commitNodeUpdate(element);
                        setCommandPart(null);
                        textChanged.current = false;
                    }
                };

                return (
                    <PartCommandsBox>
                        <PartCommandText
                            innerRef={ref => ref && ref.focus()} html={part.command || ''} part={part}
                            onBlur={e => onCommandBlur(e)} onChange={onTextChange}
                            onFocus={e => {
                                focusedField.current = part;
                            }}
                            onClick={e => { if (e.target === part.ref) focusedField.current = part; }}
                        />
                    </PartCommandsBox>
                );
            };

            const onShowCommands = (e, p) => {
                setCommandPart(p);
                document.activeElement.blur();
                if (e.target === p.ref) focusedField.current = p;
            };

            return (
                <TextParts>
                    { parts.map(p => (
                        <TextPartWrapper key={p.index}>
                            <TextPart
                                html={p.value} innerRef={ref => p.ref = ref}
                                part={p} lang={lang} spellCheck={false}
                                onBlur={e => textPartChanged(e, p)} onChange={onTextChange}
                                onFocus={(e) => {
                                    if (e.target === p.ref) focusedField.current = p;
                                    if (editor.animationEditor.showCommandsFor) setCommandPart(null);
                                }}
                                onContextMenu={e => onContextMenu(e, { part: p, lang, parts, section, field, stepFields, mouseX: e.clientX, mouseY: e.clientY, e })}
                                focusedfield={focusedField}
                                editor={editor}
                                onMouseEnter={() => { if (!focusedField.current) setHover(p); }}
                                onMouseLeave={() => { if (!focusedField.current) setHover(section); }}
                            />
                            { p.command && (
                                <CommandsBtn
                                    onClick={e => onShowCommands(e, p)}
                                    // onMouseEnter={() => setCommandPart(p)}
                                    // onMouseLeave={() => { if (focusedField.current !== p) setCommandPart(null); }}
                                >©
                                </CommandsBtn>
                            ) }
                            { editor.animationEditor.showCommandsFor === p && <PartCommands part={p} /> }
                        </TextPartWrapper>
                    ))}
                </TextParts>
            );
        };

        const Separator = ({ pos }) => {
            const sepMode = pos === 'left' ? (
                section.index === 0 ? ' '
                    : (field.sections[section.index - 1].centeringSectionIndex === section.centeringSectionIndex ? '' : '')
            ) : (
                section.index >= field.sections.length - 1 ? ' '
                    : (field.sections[section.index + 1].centeringSectionIndex !== section.centeringSectionIndex ? '' : '')
            );

            const toggleSep = () => {
                const realField = element.fields[field.index];
                document.activeElement.blur();
                if (sepMode !== ' ') {
                    if (pos === 'left') {
                        const toggledColor = sepMode == '' ? 'merge' : 'separate';
                        ['korean', 'english'].forEach(lang => {
                            const pn = `${lang}Parts`;
                            const sepIndex = section[lang].parts[0].partIndex - 1;
                            realField.parts[pn][sepIndex].color = toggledColor;
                        });
                    } else { // right
                        const toggledColor = sepMode == '' ? 'separate' : 'merge';
                        ['korean', 'english'].forEach(lang => {
                            const pn = `${lang}Parts`;
                            const sepIndex = section[lang].parts[section[lang].parts.length - 1].partIndex + 1;
                            realField.parts[pn][sepIndex].color = toggledColor;
                        });
                    }
                    editor.commitNodeUpdate(element);
                    redraw();
                }
            };

            return <SeparatorIcon onClick={toggleSep}>{sepMode}</SeparatorIcon>;
        };

        const cellSurroundMouseDown = (e) => {
            if (!e.target.getAttribute('part')) {
                e.preventDefault();
                if (editor.animationEditor.showCommandsFor)
                    editor.update({ animationEditor: { ...editor.animationEditor, showCommandsFor: null } });
                if (focusedField.current && focusedField.current.ref) {
                    focusedField.current.ref.blur();
                    focusedField.current = null;
                }
            }
        };

        return (
            <SectionCell
                onMouseEnter={() => { if (!focusedField.current) setHover(section); }}
                onMouseLeave={() => { if (!focusedField.current) setHover(null); }}
                onMouseDown={cellSurroundMouseDown}
                onContextMenu={e => onContextMenu(e, { section, field, stepFields, mouseX: e.clientX, mouseY: e.clientY, e })}
            >
                <SectionContents>
                    <Separator pos="left" />
                    <SectionText onMouseDown={cellSurroundMouseDown}>
                        <SectionTextParts lang="korean" parts={section.korean.parts} />
                        <SectionTextParts lang="english" parts={section.english.parts} />
                    </SectionText>
                    <Separator pos="right" />
                </SectionContents>
            </SectionCell>
        );
    };

    const FieldNarrationsCell = ({ field }) => (
        <NarrationsCell>
            <NarrationsList>
                { field.parts.narration.map((n, i) => (
                    <Narration key={i} onContextMenu={e => onContextMenu(e, { field, narration: n, narrationIndex: i, stepFields, mouseX: e.clientX, mouseY: e.clientY, e })}>
                        <NarrationText minRows={1} maxRows={6} defaultValue={n.value} onChange={onTextChange} onBlur={e => narrationChanged(e, field, n, i)} />
                    </Narration>
                ))}
            </NarrationsList>
        </NarrationsCell>
    );

    return (
        <Draggable
            handle="#anim-editor-handle" bounds="#root" axis="both" defaultPosition={{ x: -365, y: -200 }}>
            <AnimationEditorWrapper
                ref={editorRef} id="anim-editor" expanded={expanded}
                onMouseEnter={() => setExpanded(true)} /* onMouseLeave={e => setExpanded(e.clientX < editorRef.current.getBoundingClientRect().right)} */
            >
                <EditorTitleBar id="anim-editor-handle">
                    Animation Editor
                    <div>
                        <span onClick={e => setExpanded(!expanded)}>{ expanded ? '' : ''}</span>
                        <span onClick={e => editor.update({ animationEditor: { show: false } })}></span>
                    </div>
                </EditorTitleBar>
                <ToolBar>
                    <SearchAndReplace element={element} stepFields={stepFields} onReplace={redraw} />
                    <Hints>Cmd F = find/replace, Cmd S/U/R = update & run anim</Hints>
                </ToolBar>
                <StepsTable>
                    { stepFields.map(f => (
                        <Step key={f.index} highlight={f === highlightField}>
                            <Cell onContextMenu={e => onContextMenu(e, { field: f, stepFields, mouseX: e.clientX, mouseY: e.clientY, e })}>
                                <FieldTitle html={f.label || f.name} disabled={f.example} onChange={onTextChange} onBlur={e => fieldTitleChanged(e, f)} />
                            </Cell>
                            { f.hidden ? (
                                <Cell
                                    style={{ fontSize: 12, color: '#747474', textAlign: 'center' }}
                                    onContextMenu={e => onContextMenu(e, { field: f, stepFields, mouseX: e.clientX, mouseY: e.clientY, e })}>-- hidden step --
                                </Cell>
                            ) : (
                                <>
                                    { f.sections.map(s => (
                                        <Section key={s.index} section={s} field={f} />
                                    ))}
                                    { f.parts.narration && f.parts.narration.length && <FieldNarrationsCell field={f} /> }
                                </>
                            )}
                        </Step>
                    ))}
                </StepsTable>
                { editor.animContextMenu.open && (
                    <AnimContextMenu
                        ref={menuRef} element={element} redraw={redraw} onClose={() => {
                            if (!editor.animationEditor.showCommandsFor) focusedField.current = null;
                            if (document.activeElement) document.activeElement.blur();
                        }} />
                ) }
            </AnimationEditorWrapper>
        </Draggable>
    );
};

export default AnimationEditor;
